/**
 * Recursively converts all enum values to strings.
 *
 * In general, type `T` is assignable to `EnumsAsStrings<T>`.
 */
export type EnumsAsStrings<T> = T extends string
  ? `${T}`
  : { [K in keyof T]: EnumsAsStrings<T[K]> };

/**
 * Given a type with enum values, casts an object with string values (or just a
 * plain string) to the corresponding enum values.
 *
 * This works because string-valued enums are represented as those exact strings
 * at runtime.  However, they are technically a different type from the
 * corresponding string literals, and in particular string-valued enums extend
 * strings, so enums can be used in places that expect strings -- but not the
 * other way around.  This function fills in that gap.
 *
 * Because of that, this function will accept any combination of string-valued
 * enums and string literals, including the original type itself.
 */
export const stringsAsEnums = <T>(value: EnumsAsStrings<T>) => value as T;
